
import { useState } from "react";
import { toast } from "sonner";
import { Mail, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { readRedirectParam } from "@/utils/auth-redirect";
import { useNavigate } from "react-router-dom";
import { GoogleSignInButton } from "./google-sign-in-button";

interface PasswordLoginFormProps {
  email: string;
  password: string;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  onSuccess: () => void;
  onResetPassword: () => void;
  onCreateAccount: () => void;
  hideGoogleButton?: boolean;
}

export function PasswordLoginForm({
  email,
  password,
  setEmail,
  setPassword,
  isLoading,
  setIsLoading,
  onSuccess,
  onResetPassword,
  onCreateAccount,
  hideGoogleButton = false,
}: PasswordLoginFormProps) {
  const navigate = useNavigate();
  
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw error;
      }

      const { pulse } = await import("@/utils/pulse");
      pulse("signin");

      toast.success("Login successful!");
      
      // First call onSuccess to update any local state
      onSuccess();

      // Honour ?redirect= from RSVP / other deep links; sanitize to relative path.
      const dest = readRedirectParam() ?? "/teams";
      navigate(dest, { replace: true });

      
    } catch (error: any) {
      console.error("Error signing in with password:", error);
      toast.error("Login failed", {
        description: error.message || "An error occurred. Please try again.",
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handlePasswordLogin} className="space-y-4">
      <div className="space-y-2">
        <label 
          htmlFor="email-password" 
          className="block text-sm font-medium"
        >
          Email
        </label>
        <div className="relative">
          <Input
            id="email-password"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="coach@example.com"
            className="w-full ps-10"
            required
          />
          <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label 
            htmlFor="password" 
            className="block text-sm font-medium"
          >
            Password
          </label>
          <Button 
            type="button"
            variant="link"
            onClick={onResetPassword}
            className="p-0 h-auto text-xs"
          >
            Forgot password?
          </Button>
        </div>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="w-full"
          required
          minLength={6}
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Logging in..." : "Login"}
      </Button>
      
      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-sm text-center mb-4 text-muted-foreground">Don't have an account?</p>
        <Button 
          type="button"
          variant="outline" 
          className="w-full"
          onClick={onCreateAccount}
          disabled={isLoading}
        >
          Create Account
        </Button>
      </div>
      
      {!hideGoogleButton && <GoogleSignInButton />}
    </form>
  );
}
